import { Box, Flex, Text, Button, Input, useToast, Checkbox } from '@chakra-ui/react';
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import CTable from '../../../components/Table/table'
import Select from 'react-select'
import { examAttendaceRequest, notifyParentForExamAbsenceRequest } from '../../../modules/admin-modules/reports/Actions';
import Layout from '../../../components/dashboard/Layout';
import { getAllExamsRequest } from '../../../modules/admin-modules/exams/Actions';
import LogoutMenu from '../../../components/dashboard/Layout/logout-menu';
import { CSVLink } from 'react-csv';

// Memoized message input component to prevent re-renders
const MessageInput = React.memo(({ message, onMessageChange }) => (
    <Input
        placeholder="رسالة اختيارية (اختياري)"
        value={message}
        onChange={onMessageChange}
        flex='1'
        maxW='400px'
    />
));

const ExamAttendanceReport = ({ setAdd, setSelectedData, searchTerm }) => {
    const dispatch = useDispatch()
    const toast = useToast()
    const [examId, setExamId] = useState('')
    const [selectedStudents, setSelectedStudents] = useState([])
    const [message, setMessage] = useState('')
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(20) // Show 20 students per page

    useEffect(() => {
        if (examId) dispatch(examAttendaceRequest(examId))
    }, [examId])

    useEffect(() => {
        dispatch(getAllExamsRequest())
    }, [])
    const exams = useSelector((state) => state.exams.exams)
    const examsOptions = exams?.map((data) => {
        return { value: data.id, label: data.name }
    })

    const examAttendance = useSelector((state) => state.reports.examAttendance);
    const isLoading = useSelector((state) => state.reports.isLoading);

    const [offset, setPage] = useState(0);

    const [size, setPerPage] = useState(10);
    const [pageNumber, setPageNumber] = useState(0);
    const [selected, setSelected] = useState({
        id: ''
    })
    const colourStyles = {
        control: (styles) => ({ ...styles, width: '100%', background: '#f5f5f5', color: '#690000', borderRadius: '16px' }),
        option: (styles) => ({ ...styles, color: '#690000' }),

    };

    const viewData = (data) => {
        setSelectedData(data);
        setSelected(data)
    };

    // Handle individual student selection
    const handleStudentToggle = useCallback((studentId) => {
        setSelectedStudents(prev => {
            if (prev.includes(studentId)) {
                return prev.filter(id => id !== studentId);
            } else {
                return [...prev, studentId];
            }
        });
    }, []);

    // Handle select all for current page
    const handleSelectAllCurrentPage = useCallback((isChecked) => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const currentPageStudents = (examAttendance || []).slice(startIndex, endIndex);
        const currentPageStudentIds = currentPageStudents.map(student => student.id);
        
        setSelectedStudents(prev => {
            if (isChecked) {
                // Add current page students to selection
                const newSelection = [...prev];
                currentPageStudentIds.forEach(id => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                return newSelection;
            } else {
                // Remove current page students from selection
                return prev.filter(id => !currentPageStudentIds.includes(id));
            }
        });
    }, [currentPage, pageSize, examAttendance]);

    // Handle select all for entire dataset
    const handleSelectAll = useCallback((isChecked) => {
        if (isChecked) {
            const allStudentIds = (examAttendance || []).map(student => student.id);
            setSelectedStudents(allStudentIds);
        } else {
            setSelectedStudents([]);
        }
    }, [examAttendance]);

    // Optimized message input handler
    const handleMessageChange = useCallback((e) => {
        setMessage(e.target.value);
    }, []);

    // Handle sending notifications
    const handleSendNotifications = useCallback(() => {
        if (selectedStudents.length === 0) {
            toast({
                title: "No students selected",
                description: "Please select at least one student to send notifications",
                status: "warning",
                duration: 3000,
                isClosable: true,
            })
            return
        }

        const payload = {
            students_id: selectedStudents,
            message: message || undefined
        }

        dispatch(notifyParentForExamAbsenceRequest(payload))
        
        // Clear selections after sending
        setSelectedStudents([])
        setMessage('')
    }, [selectedStudents, message, dispatch, toast]);

    // Paginate the data
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return (examAttendance || []).slice(startIndex, endIndex);
    }, [examAttendance, currentPage, pageSize]);

    // Calculate total pages
    const totalPages = useMemo(() => {
        return Math.ceil((examAttendance || []).length / pageSize);
    }, [examAttendance, pageSize]);

    // Check if all students are selected
    const allSelected = useMemo(() => {
        return selectedStudents.length === (examAttendance || []).length && (examAttendance || []).length > 0;
    }, [selectedStudents, examAttendance]);

    // Check if current page is fully selected
    const currentPageSelected = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const currentPageStudents = (examAttendance || []).slice(startIndex, endIndex);
        const currentPageStudentIds = currentPageStudents.map(student => student.id);
        return currentPageStudentIds.every(id => selectedStudents.includes(id)) && currentPageStudentIds.length > 0;
    }, [selectedStudents, examAttendance, currentPage, pageSize]);

    const headers = [
        { label: 'name', key: 'name' },
        { label: 'phone', key: 'phone' },
        { label: 'parent_phone', key: 'parent_phone' },
    ];

    // Table data for current page only
    const tableData = paginatedData.map((data, index) => ({
        ...data,
        index: `#${(currentPage - 1) * pageSize + index + 1}`,
        id: data?.id,
        name: data?.name,
        parent_phone: data?.parent_phone,
        phone: data?.phone,
        select: (
            <input
                type="checkbox"
                checked={selectedStudents.includes(data.id)}
                onChange={() => handleStudentToggle(data.id)}
                style={{ width: '16px', height: '16px' }}
            />
        ),
    }));

    const columns = [
        {
            Header: () => (
                <input
                    type="checkbox"
                    checked={currentPageSelected}
                    onChange={(e) => handleSelectAllCurrentPage(e.target.checked)}
                    style={{ width: '16px', height: '16px' }}
                />
            ),
            accessor: "select",
        },
        {
            Header: 'رقم الطالب',
            accessor: 'index'
        },
        {
            Header: 'اسم الطالب',
            accessor: 'name'
        },
        {
            Header: ' رقم هاتف ولي الامر',
            accessor: 'parent_phone'
        },
        {
            Header: ' رقم هاتف الطالب',
            accessor: 'phone'
        },
    ];

    // Pagination controls
    const PaginationControls = () => (
        <Flex justifyContent="space-between" alignItems="center" mt={4} mb={4}>
            <Text fontSize="14px" color="gray.600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, (examAttendance || []).length)} of {(examAttendance || []).length} students
            </Text>
            <Flex gap={2}>
                <Button
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                >
                    Previous
                </Button>
                <Text fontSize="14px" alignSelf="center">
                    Page {currentPage} of {totalPages}
                </Text>
                <Button
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                >
                    Next
                </Button>
            </Flex>
        </Flex>
    );

    return (
        <Layout >
            <div style={{ minHeight: '400px' }}>

                <Flex w='100%' my='30px' justifyContent='space-between' alignItems='center' position='relative' zIndex='10000000'>

                    <Text fontSize='24px'
                        fontWeight='600' color='main_1'>
                        تقرير غياب الامتحانات
                    </Text>
                    <Flex w='30%' justifyContent='space-between' alignItems='center' >

                        <Select options={examsOptions} isMulti={false} onChange={(data) => {
                            setExamId(data?.value)
                        }}
                            styles={colourStyles}
                            placeholder='Select Exam'
                        />
                        <Box mx='10px' border='1px solid gray' borderRadius='8px' padding='10px'>
                            <CSVLink
                                headers={headers}
                                data={tableData?.length != 0 ? tableData : []}
                                filename={"exam-report.csv"}
                                className="btn btn-primary"
                                target="_blank"
                            >
                                Download
                            </CSVLink>
                        </Box>
                        <LogoutMenu />
                    </Flex>
                </Flex>

                {/* Select All Controls */}
                {examAttendance && examAttendance.length > 0 && (
                    <Box mb={4} p={4} bg="gray.50" borderRadius="md">
                        <Flex justifyContent="space-between" alignItems="center">
                            <Text fontSize="16px" fontWeight="500">
                                Exam Attendance ({examAttendance.length} students)
                            </Text>
                            <Flex gap={4} alignItems="center">
                                <Checkbox
                                    isChecked={allSelected}
                                    isIndeterminate={selectedStudents.length > 0 && !allSelected}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                >
                                    Select All Students
                                </Checkbox>
                                <Text fontSize="14px" color="gray.600">
                                    {selectedStudents.length} selected
                                </Text>
                            </Flex>
                        </Flex>
                    </Box>
                )}

                {/* Notification Section */}
                {selectedStudents.length > 0 && (
                    <Box mb='20px' p='20px' bg='orange.50' borderRadius='8px' border='1px solid' borderColor='orange.200'>
                        <Text mb='10px' fontWeight='600' color='orange.800'>
                            إرسال إشعار لولي الأمر ({selectedStudents.length} طالب محدد)
                        </Text>
                        <Flex gap='10px' alignItems='center'>
                            <MessageInput 
                                message={message}
                                onMessageChange={handleMessageChange}
                            />
                            <Button
                                colorScheme='orange'
                                onClick={handleSendNotifications}
                                isLoading={isLoading}
                                loadingText="جاري الإرسال..."
                            >
                                إرسال الإشعار
                            </Button>
                        </Flex>
                    </Box>
                )}

                {/* Pagination Controls */}
                {examAttendance && examAttendance.length > 0 && <PaginationControls />}

                <CTable
                    selectedData={viewData}
                    Columns={columns}
                    Data={tableData}
                    path={'openModel'}
                    Actions
                    Title={``}
                    subTitle=''
                    btnTitle=""
                    placeHolder=""
                    noSearchBar={true}
                    noFilter={true}
                    footerBtnTitle
                    filterList
                    addButton
                    addButtonTitle=""
                    setPage={setPage}
                    setPerPage={setPerPage}
                    currentpage={pageNumber}
                    setPageNumber={setPageNumber}
                    noSecondSearchBar={true}
                    isLoading={isLoading}
                    perPage={size}
                    totalPage={Math.ceil(10 / 50)}

                />
            </div>
        </Layout>

    )
}

export default ExamAttendanceReport